import { z } from 'zod';

/**
 * Maximum number of media units a single supplier-photo ZIP export may resolve to.
 * Mirrors `BULK_DOWNLOAD_MAX_UNITS`. Exported so UI guards stay in sync. Covers REQ-9.4.
 */
export const SUPPLIER_PHOTO_ZIP_MAX_UNITS = 200;

/**
 * Request schema for the supplier-photo ZIP manifest endpoint. Accepts EXACTLY ONE
 * resolution selector: `supplierId`, `supplierNumber`, or `grwIds`. Covers REQ-9.4.
 */
export const SupplierPhotoManifestRequestSchema = z
  .object({
    supplierId: z.number().int().positive().optional(),
    supplierNumber: z.string().min(1).optional(),
    grwIds: z.array(z.string().min(1)).min(1).optional(),
  })
  .superRefine((val, ctx) => {
    const selectorCount = [val.supplierId, val.supplierNumber, val.grwIds].filter(
      (selector) => selector !== undefined,
    ).length;
    if (selectorCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Exactly one of supplierId, supplierNumber, or grwIds must be provided',
      });
    }
  });

export type SupplierPhotoManifestRequest = z.infer<typeof SupplierPhotoManifestRequestSchema>;

const SupplierPhotoSchema = z.object({
  id: z.number(),
  filename: z.string(),
  sizeBytes: z.number(),
  downloadUrl: z.string().url(),
});

const SupplierPhotoUnitSchema = z.object({
  unitId: z.number(),
  grwId: z.string(),
  supplierNumber: z.string().nullable(),
  photos: z.array(SupplierPhotoSchema),
});

/**
 * Response schema for the supplier-photo ZIP manifest endpoint. Mirrors the bulk-download
 * manifest shape but carries photo entries and per-unit supplier numbers. Covers REQ-9.4.
 */
export const SupplierPhotoManifestResponseSchema = z.object({
  units: z.array(SupplierPhotoUnitSchema),
  totalFiles: z.number(),
  totalBytes: z.number(),
  unitsWithoutPhotos: z.array(z.object({ unitId: z.number(), grwId: z.string() })),
});

export type SupplierPhotoManifestResponse = z.infer<typeof SupplierPhotoManifestResponseSchema>;
